import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ServiceCard } from "./ServiceCard";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Rocket, Globe2, Smartphone, Settings, Gauge, Bot, type LucideIcon } from "lucide-react";

const SERVICES: { key: string; icon: LucideIcon; badge?: boolean }[] = [
  { key: "landingPages", icon: Rocket },
  { key: "institutionalSites", icon: Globe2 },
  { key: "mobileApps", icon: Smartphone },
  { key: "customSystems", icon: Settings },
  { key: "performance", icon: Gauge },
  { key: "ai", icon: Bot, badge: true },
];

export function ServicesShowcase() {
  const { t } = useTranslation();
  const [api, setApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);

  const handleSelect = useCallback(() => {
    if (!api) return;
    setActiveIndex(api.selectedScrollSnap());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    setActiveIndex(api.selectedScrollSnap());
    api.on("select", handleSelect);
    api.on("reInit", handleSelect);
    return () => {
      api.off("select", handleSelect);
    };
  }, [api, handleSelect]);

  const renderCard = (service: (typeof SERVICES)[number]) => (
    <ServiceCard
      icon={service.icon}
      title={t(`services.${service.key}.title`)}
      description={t(`services.${service.key}.description`)}
      badge={service.badge ? t(`services.${service.key}.badge`) : undefined}
    />
  );

  return (
    <>
      {/* Mobile: swipeable carousel, one service at a time */}
      <div className="md:hidden">
        <Carousel opts={{ align: "center", loop: false }} setApi={setApi}>
          <CarouselContent className="-ml-4 pt-7">
            {SERVICES.map((service) => (
              <CarouselItem key={service.key} className="basis-[86%] pl-4">
                {renderCard(service)}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <div className="flex justify-center gap-2 mt-6">
          {SERVICES.map((service, index) => (
            <button
              key={service.key}
              onClick={() => api?.scrollTo(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                activeIndex === index ? "w-6 bg-primary" : "w-2 bg-primary/30"
              }`}
              aria-label={`Ver serviço ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Tablet/Desktop: full grid */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.map((service) => (
          <div key={service.key}>{renderCard(service)}</div>
        ))}
      </div>
    </>
  );
}
